import React, { useState } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Container,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from './context/AuthContext';
import SidebarMenu from './components/SidebarMenu';
import SessionKeepAlive from './components/SessionKeepAlive';

import EmpresasPage from './pages/EmpresasPage';
import PessoasPage from './pages/PessoasPage';
import ImoveisPage from './pages/ImoveisPage';
import VeiculosPage from './pages/VeiculosPage';
import UsuariosPage from './pages/UsuariosPage';
import MoradoresPage from './pages/MoradoresPage';
import VisitantesPage from './pages/VisitantesPage';
import AcessosPage from './pages/AcessosPage';
import AcessosLiberadosPage from './pages/AcessosLiberadosPage';
import LoginPage from './pages/LoginPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import EncomendasPage from './pages/EncomendasPage';
import ProprietariosPage from './pages/ProprietariosPage';
import ConsumoAguaPage from './pages/ConsumoAguaPage';
import PontoPage from './pages/PontoPage';
import FuncionariosPage from './pages/FuncionariosPage';
import DashboardPage from './pages/DashboardPage';
import ImportacaoCadastrosPage from './pages/ImportacaoCadastrosPage';
import ManutencoesPage from './pages/ManutencoesPage';
import CadastrosManutencaoPage from './pages/CadastrosManutencaoPage';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.tipo_usuario)) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography variant="h5" color="error">
          Você não tem permissão para acessar esta página.
        </Typography>
      </Container>
    );
  }

  return children;
};

const getDefaultPathByRole = (tipoUsuario) => {
  if (tipoUsuario === 'portaria' || tipoUsuario === 'admin' || tipoUsuario === 'sindico' || tipoUsuario === 'padrao') return '/dashboard';
  return '/login';
};

const AppShell = ({ user, logout, switchCondominio }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const toggleMobileMenu = () => setMobileOpen((prev) => !prev);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: alpha(theme.palette.background.paper, 0.78),
          color: theme.palette.text.primary,
          borderBottom: '1px solid',
          borderColor: theme.palette.divider,
          backdropFilter: 'blur(16px)'
        }}
      >
        <Toolbar sx={{ minHeight: 72 }}>
          <IconButton
            color="inherit"
            aria-label="abrir menu"
            edge="start"
            onClick={toggleMobileMenu}
            sx={{ mr: 2, display: { xs: 'inline-flex', md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h5"
              component="div"
              sx={{ fontWeight: 900, lineHeight: 1, color: 'primary.main' }}
            >
              coNDu
            </Typography>
          </Box>
          {user.condominios?.length > 1 && (
            <FormControl size="small" sx={{ minWidth: { xs: 150, sm: 260 }, mr: 1 }}>
              <InputLabel id="condominio-ativo-label">Condomínio</InputLabel>
              <Select
                labelId="condominio-ativo-label"
                value={user.empresa_id || ''}
                label="Condomínio"
                onChange={async (event) => {
                  try {
                    await switchCondominio(event.target.value);
                    window.location.reload();
                  } catch (error) {
                    console.error('Erro ao trocar condomínio:', error);
                  }
                }}
              >
                {user.condominios.map((condominio) => (
                  <MenuItem key={condominio.empresa_id} value={condominio.empresa_id}>
                    {condominio.empresa_nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Toolbar>
      </AppBar>

      <SidebarMenu
        user={user}
        logout={logout}
        mobileOpen={mobileOpen}
        onMobileToggle={toggleMobileMenu}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: { xs: 2, sm: 3, md: 4 },
          py: 3,
          background: `radial-gradient(circle at top right, ${alpha(theme.palette.primary.main, 0.08)} 0, transparent 38%), radial-gradient(circle at bottom left, ${alpha(theme.palette.text.primary, 0.05)} 0, transparent 34%), linear-gradient(180deg, var(--color-background) 0%, var(--color-surface-alt) 100%)`
        }}
      >
        <Toolbar sx={{ minHeight: 72 }} />
        <Routes>
          <Route path="*" element={<Navigate to={getDefaultPathByRole(user.tipo_usuario)} replace />} />
          <Route path="/change-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />

          <Route path="/dashboard" element={<ProtectedRoute allowedRoles={['admin', 'sindico', 'portaria', 'padrao']}><DashboardPage /></ProtectedRoute>} />

          <Route path="/empresas" element={<ProtectedRoute allowedRoles={['admin', 'sindico']}><EmpresasPage /></ProtectedRoute>} />
          <Route path="/pessoas" element={<ProtectedRoute allowedRoles={['admin', 'sindico', 'portaria']}><PessoasPage /></ProtectedRoute>} />
          <Route path="/importacoes" element={<ProtectedRoute allowedRoles={['admin', 'sindico']}><ImportacaoCadastrosPage /></ProtectedRoute>} />
          <Route path="/imoveis" element={<ProtectedRoute allowedRoles={['admin', 'sindico']}><ImoveisPage /></ProtectedRoute>} />
          <Route path="/veiculos" element={<ProtectedRoute allowedRoles={['admin', 'sindico', 'portaria']}><VeiculosPage /></ProtectedRoute>} />
          <Route path="/usuarios" element={<ProtectedRoute allowedRoles={['admin']}><UsuariosPage /></ProtectedRoute>} />
          <Route path="/moradores" element={<ProtectedRoute allowedRoles={['admin', 'sindico', 'portaria']}><MoradoresPage /></ProtectedRoute>} />
          <Route path="/proprietarios" element={<ProtectedRoute allowedRoles={['admin', 'sindico']}><ProprietariosPage /></ProtectedRoute>} />
          <Route path="/visitantes" element={<ProtectedRoute allowedRoles={['admin', 'sindico', 'padrao']}><VisitantesPage /></ProtectedRoute>} />
          <Route path="/acessos" element={<ProtectedRoute allowedRoles={['admin', 'sindico', 'portaria']}><AcessosPage /></ProtectedRoute>} />
          <Route path="/acessos-liberados" element={<ProtectedRoute allowedRoles={['admin', 'sindico', 'portaria']}><AcessosLiberadosPage /></ProtectedRoute>} />
          <Route path="/encomendas" element={<ProtectedRoute allowedRoles={['admin', 'sindico', 'portaria']}><EncomendasPage /></ProtectedRoute>} />
          <Route path="/consumo-agua" element={<ProtectedRoute allowedRoles={['admin', 'sindico', 'portaria']}><ConsumoAguaPage /></ProtectedRoute>} />
          <Route path="/funcionarios" element={<ProtectedRoute allowedRoles={['admin', 'sindico', 'portaria']}><FuncionariosPage /></ProtectedRoute>} />
          <Route path="/ponto" element={<ProtectedRoute allowedRoles={['admin', 'sindico', 'portaria']}><PontoPage /></ProtectedRoute>} />
          <Route path="/cadastros-manutencao" element={<ProtectedRoute allowedRoles={['admin', 'sindico']}><CadastrosManutencaoPage /></ProtectedRoute>} />
          <Route path="/manutencoes" element={<ProtectedRoute allowedRoles={['admin', 'sindico']}><ManutencoesPage /></ProtectedRoute>} />
        </Routes>
      </Box>
      <SessionKeepAlive />
    </Box>
  );
};

function App() {
  const { user, loading, logout, switchCondominio } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<Navigate to={user ? getDefaultPathByRole(user.tipo_usuario) : '/login'} replace />} />
        <Route path="/*" element={user ? <AppShell user={user} logout={logout} switchCondominio={switchCondominio} /> : <Navigate to="/login" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
